import { env } from "@/shared/constants/env";

// Chargeur du SDK Google Maps JavaScript.
//
// ## Pourquoi un chargeur maison plutôt que `@googlemaps/js-api-loader`
// Le besoin tient en quarante lignes : injecter un <script>, ne le faire qu'une
// fois, et rendre la même promesse à tous les appelants. La bibliothèque
// officielle ajoute une gestion de versions et de bibliothèques optionnelles
// dont aucune carte de l'app ne se sert. Une dépendance de moins à suivre.
//
// ## Pourquoi pas un <script> dans index.html
// Le SDK serait chargé sur TOUTES les pages, y compris celles sans carte
// (connexion, profil, portefeuille). Or Maps JavaScript est facturé au
// CHARGEMENT DE CARTE : le script seul ne coûte rien, mais le télécharger
// partout ralentit chaque page pour rien. Ici il n'arrive qu'au premier montage
// d'une carte, et une seule fois pour toute la session.

const SDK_VERSION = "quarterly"; // canal stable, mis à jour tous les trimestres

export class GoogleMapsUnavailableError extends Error {}

let loadPromise: Promise<typeof google.maps> | null = null;

/**
 * Charge le SDK et résout l'espace de noms `google.maps`.
 *
 * Rejette avec `GoogleMapsUnavailableError` si aucune clé n'est configurée, ou
 * si le script ne se charge pas (clé invalide, référent refusé, réseau, API non
 * activée). Les appelants affichent alors un message explicite : une carte
 * Google sans clé valide se peint en gris avec un filigrane d'erreur, ce qui se
 * lit comme un bug de l'app plutôt que comme un défaut de configuration.
 */
export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (loadPromise) return loadPromise;

  if (!env.googleMapsApiKey) {
    // Ce rejet n'est PAS mis en cache : garder une promesse échouée
    // interdirait tout nouvel essai après un rechargement à chaud en dev.
    return Promise.reject(
      new GoogleMapsUnavailableError("VITE_GOOGLE_MAPS_API_KEY absente — voir .env.example"),
    );
  }

  loadPromise = new Promise((resolve, reject) => {
    // Le SDK a pu être injecté par ailleurs (rechargement à chaud, extension).
    if (typeof google !== "undefined" && google.maps) {
      resolve(google.maps);
      return;
    }

    // ⚠️ Le `load` du <script> NE SUFFIT PAS avec `loading=async`.
    // L'URL ne renvoie qu'un bootstrap, qui va lui-même chercher le vrai
    // `main.js` : au moment où `load` se déclenche, `google.maps` n'existe
    // pas encore. S'y fier faisait échouer TOUTES les cartes, avec le message
    // « Carte indisponible » alors que la clé était parfaitement valide.
    //
    // Google prévoit `callback` pour ça : la fonction globale n'est appelée
    // qu'une fois l'espace de noms réellement prêt.
    const callbackName = "__alomainGoogleMapsReady";
    const win = window as unknown as Record<string, unknown>;

    win[callbackName] = () => {
      delete win[callbackName];
      resolve(google.maps);
    };

    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: env.googleMapsApiKey,
      v: SDK_VERSION,
      loading: "async", // recommandé par Google : ne bloque pas le rendu
      callback: callbackName,
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;

    script.addEventListener("error", () => {
      // Un échec réseau doit rester rattrapable au montage suivant.
      delete win[callbackName];
      loadPromise = null;
      reject(new GoogleMapsUnavailableError("Chargement du SDK Google Maps impossible"));
    });

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Charge une bibliothèque optionnelle du SDK (`places`, `geocoding`, `marker`…).
 *
 * Le bootstrap ne contient que le noyau : `google.maps.places` n'existe pas tant
 * qu'on ne l'a pas demandé. `importLibrary` est idempotent côté Google — deux
 * appels ne téléchargent qu'une fois.
 */
export async function loadGoogleMapsLibrary<T>(name: string): Promise<T> {
  const maps = await loadGoogleMaps();
  return maps.importLibrary(name) as Promise<T>;
}
